/**
 * Money rendering — the ONE place amounts hit the screen.
 *
 * Uses `formatAmount` (grouped integer) or `formatSignedAmount` (with U+2212
 * minus + leading + on positive) from `lib/money.ts`. `tabular-nums` keeps
 * digit columns aligned — essential for the ledger's right-aligned amount
 * column and the summary cards.
 *
 * Sign colour is opt-in. The table column uses `sign='auto'` so the same
 * component handles the prototype's "+income green / −expense red" coloured
 * total in one place; the summary cards pass `sign='none'` and decide the
 * tone (debit-green / credit-red / sign-coloured net) at the call-site.
 *
 * Currency is rendered as a low-emphasis sibling label resolved via i18n
 * (`currency.UZS`, `currency.USD`) — never a hard-coded "сўм" / "USD" literal.
 */

import type { Currency } from '@/features/accounting/api/contracts';
import { formatAmount, formatSignedAmount } from '@/features/accounting/lib/money';
import { CURRENCY_LABEL_KEY } from '@/features/accounting/model/status-meta';
import { useTranslation } from 'react-i18next';

type SignMode = 'none' | 'auto' | 'force-positive' | 'force-negative'

interface MoneyTextProps {
  amount: number
  currency?: Currency
  sign?: SignMode
  /** Apply +green / −red to the figure when `sign !== 'none'`. */
  colored?: boolean
  className?: string
}

function renderAmount(amount: number, sign: SignMode): string {
  switch (sign) {
    case 'none':
      return formatAmount(amount)
    case 'auto':
      return formatSignedAmount(amount)
    case 'force-positive':
      return formatSignedAmount(Math.abs(amount))
    case 'force-negative':
      return formatSignedAmount(-Math.abs(amount))
  }
}

function colourClass(amount: number, sign: SignMode): string {
  if (sign === 'none') return ''
  const effective = sign === 'force-negative' ? -1 : sign === 'force-positive' ? 1 : amount
  if (effective > 0) return 'text-(--text-success)'
  if (effective < 0) return 'text-(--text-error)'
  return ''
}

export function MoneyText({
  amount,
  currency,
  sign = 'none',
  colored = false,
  className = '',
}: MoneyTextProps) {
  const { t } = useTranslation('accounting')
  const tone = colored ? colourClass(amount, sign) : ''
  return (
    <span
      className={['inline-flex items-baseline gap-1 tabular-nums', tone, className]
        .filter(Boolean)
        .join(' ')}
    >
      <span className='font-semibold'>{renderAmount(amount, sign)}</span>
      {currency && (
        <span className='text-xs font-normal text-(--text-secondary)'>
          {t(CURRENCY_LABEL_KEY[currency])}
        </span>
      )}
    </span>
  )
}
